import { useRef, useState } from 'react';
import { Check, Send } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Illustration } from '../../components/Illustration';
import GearTag from '../../components/treatment/GearTag';
import { prefersReducedMotion, usePlayOnView } from './motion';
import { ink, stock } from './paper';

// The Teams chapter's two pictures. The crew drawing is painted on surface
// and the accounting one on canvas, so each card is the colour its art needs. Roles, never names: the page shows no
// one's crew. The crew card shows who is booked and for how long; it says
// nothing about rates; neither card mentions markup.

const SHOOT_DAYS = 3; // the job's length, a cell a day on each crew row
const CREW = [
  { role: 'Gaffer', days: 3 },
  { role: 'Focus puller', days: 3 },
  { role: 'Key grip', days: 2 },
];
const HIRES = [
  { name: 'Drone and pilot' },
  { name: 'Water housing' },
];

function useRun(threshold) {
  const ref = useRef(null);
  const run = usePlayOnView(ref, threshold);
  const [instant] = useState(prefersReducedMotion);
  return { ref, run, instant };
}

// "Your crew and your suppliers": a crew card with who is booked on the job,
// and for how many of its days; and the hires your suppliers bring in
export function CrewAndSuppliers() {
  const { ref, run, instant } = useRun(0.4);
  const crewDays = CREW.reduce((sum, c) => sum + c.days, 0);
  return (
    <div ref={ref} className="grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-4 lg:gap-6">
      <div className="rounded-highlight bg-surface text-foreground p-6 sm:p-8 flex flex-col gap-6">
        <div className="flex items-center gap-4">
          <Illustration name="crew" className="w-[72px] shrink-0 aspect-square" />
          <div>
            <h3 className="text-xl font-normal">Crew</h3>
            <p className="text-sm text-muted-foreground">Booked on the job, day by day</p>
          </div>
        </div>
        <ul className="flex flex-col gap-5">
          {CREW.map((c, i) => (
            <li key={c.role} className="flex flex-col gap-2">
              <span>{c.role}<span className="text-muted-foreground"> · {c.days} {c.days === 1 ? 'day' : 'days'}</span></span>
              {/* A cell for each day of the shoot, filled for the days they're booked */}
              <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${SHOOT_DAYS}, minmax(0, 1fr))` }} aria-hidden="true">
                {Array.from({ length: SHOOT_DAYS }, (_, d) => (
                  <span
                    key={d}
                    className={cn(
                      'h-3 rounded-pill',
                      d < c.days ? 'bg-foreground/70' : 'bg-mist',
                      !instant && 'transition-opacity duration-300',
                      run || d >= c.days ? 'opacity-100' : 'opacity-0',
                    )}
                    style={{ transitionDelay: run && !instant ? `${200 + i * 220 + d * 120}ms` : '0ms' }}
                  />
                ))}
              </div>
            </li>
          ))}
        </ul>
        <p className="text-sm text-muted-foreground">{crewDays} crew days across the {SHOOT_DAYS}-day shoot</p>
      </div>

      <div className="rounded-tile bg-surface text-foreground p-6 sm:p-8 flex flex-col gap-6">
        <div>
          <h3 className="text-xl font-normal">Suppliers</h3>
          <p className="text-sm text-muted-foreground">The gear you don’t own, hired in from your suppliers</p>
        </div>
        <ul className="flex flex-col gap-4">
          {HIRES.map((h, i) => (
            <li
              key={h.name}
              className={cn(
                'flex items-center gap-3',
                !instant && 'transition-[transform,opacity] duration-500 ease-out',
                run ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6',
              )}
              style={{ transitionDelay: run && !instant ? `${400 + i * 250}ms` : '0ms' }}
            >
              <ul><GearTag name={h.name} hire /></ul>
            </li>
          ))}
        </ul>
        <p
          className={cn('mt-auto flex items-center gap-2 text-sm transition-opacity duration-300', run ? 'opacity-100' : 'opacity-0')}
          style={{ transitionDelay: run && !instant ? '1200ms' : '0ms' }}
        >
          <Check className="w-4 h-4 text-moss" aria-hidden="true" /> On the quote, beside your own gear
        </p>
      </div>
    </div>
  );
}

const POS = [
  { role: 'Gaffer', paper: 'wheat' },
  { role: 'Focus puller', paper: 'tide' },
  { role: 'Key grip', paper: 'sage' },
];

// "Send POs": a purchase order per crew member, sent in one go, then every
// one goes to Xero as a bill
export function PurchaseOrders({ crewSuper = false }) {
  const { ref, run, instant } = useRun(0.5);
  const step = (ms) => (run && !instant ? `${ms}ms` : '0ms');
  return (
    <div ref={ref} className="grid md:grid-cols-[minmax(0,7fr)_minmax(0,4fr)] gap-6 lg:gap-10 items-center">
      <ul className="grid grid-cols-3 gap-3 sm:gap-4">
        {POS.map((po, i) => (
          <li
            key={po.role}
            className={cn(
              'relative rounded-tile p-3 sm:p-4 flex flex-col gap-2.5 min-h-[176px] min-w-0',
              !instant && 'transition-[transform,opacity] duration-500 ease-[cubic-bezier(.3,.7,.25,1)]',
              run ? 'opacity-100 translate-y-0 rotate-0' : 'opacity-0 translate-y-6 rotate-2',
            )}
            style={{ ...stock(po.paper), transitionDelay: step(i * 180) }}
          >
            <p className="text-sm">PO</p>
            <p className="text-xs opacity-80 truncate">{po.role}</p>
            {[80, 62, 70].map((w) => (
              <span key={w} className="h-1.5 rounded-pill" style={{ ...ink(35), width: `${w}%` }} aria-hidden="true" />
            ))}
            <span className="mt-auto h-2 w-12 self-end rounded-pill" style={ink(75)} aria-hidden="true" />
            {/* Sent, all at once */}
            <span
              className={cn(
                'absolute -top-2 -right-2 flex items-center gap-1 rounded-pill bg-background text-foreground px-2 py-0.5 text-xs shadow-sm',
                !instant && 'transition-[transform,opacity] duration-300',
                run ? 'opacity-100 scale-100' : 'opacity-0 scale-75',
              )}
              style={{ transitionDelay: step(1000) }}
            >
              <Send className="w-3 h-3" aria-hidden="true" /> Sent
            </span>
          </li>
        ))}
      </ul>

      {/* On canvas: the drawing is painted on it */}
      <div className="rounded-highlight bg-background p-6 flex flex-col gap-4">
        <Illustration name="invoicing" className="w-32" />
        <p className="text-lg">Crew bills, in Xero</p>
        <ul className="flex flex-col gap-2 text-sm">
          {POS.map((po, i) => (
            <li
              key={po.role}
              className={cn('flex items-center gap-2 transition-opacity duration-300', run ? 'opacity-100' : 'opacity-0')}
              style={{ transitionDelay: step(1500 + i * 220) }}
            >
              <Check className="w-4 h-4 text-moss" aria-hidden="true" />
              {po.role}, drafted
            </li>
          ))}
          {crewSuper && (
            <li
              className={cn('flex items-center gap-2 transition-opacity duration-300', run ? 'opacity-100' : 'opacity-0')}
              style={{ transitionDelay: step(1500 + POS.length * 220) }}
            >
              <Check className="w-4 h-4 text-moss" aria-hidden="true" />
              Super, worked out
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
